/**
 * Users Service
 * Handles all user-related database operations
 */

import { supabase } from '@/supabase.config';
import { NotFoundError, DatabaseError } from '@/lib/errors';
import { User, Role } from '@/models/user';


/**
 * Get all active users with relations
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllUsers(): Promise<User[]> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        role:roles(id, name, code),
        region:regions(id, name, code),
        district:districts(id, name, code)
      `)
      .eq('is_active', true)
      .order('full_name', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as User[];
  } catch (error) {
    throw new DatabaseError('Failed to fetch users', error);
  }
}

/**
 * Get a user by ID with relations
 * @throws {NotFoundError} If user is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getUserById(id: string): Promise<User | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        role:roles(id, name, code),
        region:regions(id, name, code),
        district:districts(id, name, code)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`User with ID ${id} not found`);
      }
      throw error;
    }

    return data as unknown as User;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch user with ID ${id}`, error);
  }
}

/**
 * Get user profile by Supabase Auth ID
 * This is used to get the user's profile data from the public.users table
 * using their auth.users ID
 * @throws {NotFoundError} If user is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getUserProfileByAuthId(authId: string): Promise<User | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        role:roles(*),
        region:regions(id, name, code),
        district:districts(id, name, code)
      `)
      .eq('id', authId)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`User with Auth ID ${authId} not found`);
      }
      throw error;
    }

    return data as unknown as User;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch user profile by auth ID ${authId}`, error);
  }
}

// ============================================================================
// ROLES
// ============================================================================

/**
 * Get all active roles
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllRoles(): Promise<Role[]> {
  try {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []) as Role[];
  } catch (error) {
    throw new DatabaseError('Failed to fetch roles', error);
  }
}

/**
 * Get a role by ID
 * @throws {NotFoundError} If role is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getRoleById(id: string): Promise<Role | null> {
  try {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Role with ID ${id} not found`);
      }
      throw error;
    }

    return data as Role;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch role with ID ${id}`, error);
  }
}
